import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { RotateCcw, Save } from "lucide-react";

const Field = ({ label, id, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="overline text-slate-500">{label}</Label>
    {children}
  </div>
);

const ColorField = ({ label, id, value, onChange }) => (
  <Field label={label} id={id}>
    <div className="flex items-center gap-2">
      <input
        type="color"
        data-testid={`${id}-swatch`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-10 rounded-sm border border-slate-200 cursor-pointer p-0.5 bg-white"
      />
      <Input
        id={id}
        data-testid={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="font-label text-sm rounded-sm focus-visible:ring-[#00857C]"
      />
    </div>
  </Field>
);

export const ConfigPanel = ({ branding, setBranding, onSave, onReset, saving }) => {
  const upd = (k) => (v) => setBranding((b) => ({ ...b, [k]: v }));
  const updE = (k) => (e) => upd(k)(e.target.value);

  return (
    <aside
      data-testid="config-panel"
      className="w-80 shrink-0 bg-white border-r border-slate-200 h-screen sticky top-0 flex flex-col"
    >
      <div className="px-6 py-5 border-b border-slate-100">
        <h2 className="font-head text-lg font-bold text-[#0B2545]">Branding Config</h2>
        <p className="text-xs text-slate-500 mt-0.5">Edits reflect live. Save, then export.</p>
      </div>

      <div className="flex-1 overflow-y-auto thin-scroll px-6 py-5 space-y-5">
        <Field label="Document Title" id="cfg-title">
          <Input id="cfg-title" data-testid="cfg-title" value={branding.document_title} onChange={updE("document_title")} className="rounded-sm focus-visible:ring-[#00857C]" />
        </Field>
        <Field label="Subtitle" id="cfg-subtitle">
          <Input id="cfg-subtitle" data-testid="cfg-subtitle" value={branding.document_subtitle} onChange={updE("document_subtitle")} className="rounded-sm focus-visible:ring-[#00857C]" />
        </Field>

        <Separator />
        <ColorField label="Primary Colour" id="cfg-primary" value={branding.primary_color} onChange={upd("primary_color")} />
        <ColorField label="Secondary Colour" id="cfg-secondary" value={branding.secondary_color} onChange={upd("secondary_color")} />

        <Separator />
        <Field label="Logo Wordmark" id="cfg-logo">
          <Input id="cfg-logo" data-testid="cfg-logo" value={branding.logo_text} onChange={updE("logo_text")} className="rounded-sm focus-visible:ring-[#00857C]" />
        </Field>
        <Field label="Logo Image URL (optional)" id="cfg-logo-url">
          <Input id="cfg-logo-url" data-testid="cfg-logo-url" placeholder="https://…" value={branding.logo_url} onChange={updE("logo_url")} className="rounded-sm text-xs focus-visible:ring-[#00857C]" />
        </Field>
        <Field label="Cover Image URL" id="cfg-cover">
          <Input id="cfg-cover" data-testid="cfg-cover" value={branding.cover_image} onChange={updE("cover_image")} className="rounded-sm text-xs focus-visible:ring-[#00857C]" />
        </Field>

        <Separator />
        <Field label="Author / Contact" id="cfg-author">
          <Input id="cfg-author" data-testid="cfg-author" value={branding.author} onChange={updE("author")} className="rounded-sm focus-visible:ring-[#00857C]" />
        </Field>
        <Field label="Author Role" id="cfg-author-role">
          <Input id="cfg-author-role" data-testid="cfg-author-role" value={branding.author_role} onChange={updE("author_role")} className="rounded-sm focus-visible:ring-[#00857C]" />
        </Field>
        <Field label="Version" id="cfg-version">
          <Input id="cfg-version" data-testid="cfg-version" value={branding.version} onChange={updE("version")} className="rounded-sm focus-visible:ring-[#00857C]" />
        </Field>
        <Field label="Footer Text" id="cfg-footer">
          <Textarea id="cfg-footer" data-testid="cfg-footer" value={branding.footer_text} onChange={updE("footer_text")} rows={3} className="rounded-sm text-xs focus-visible:ring-[#00857C]" />
        </Field>
      </div>

      <div className="border-t border-slate-100 p-4 flex gap-2">
        <Button data-testid="reset-branding-button" variant="outline" onClick={onReset} className="rounded-sm border-slate-300">
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button data-testid="save-branding-button" onClick={onSave} disabled={saving} className="flex-1 rounded-sm bg-[#0B2545] hover:bg-[#0B2545]/90 text-white font-head">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save Branding"}
        </Button>
      </div>
    </aside>
  );
};
